import { buildApiUrl } from './api'

export interface Customer {
  id: number
  name?: string  // Legacy field - will be null for migrated customers
  first_name?: string
  last_name?: string
  email: string
  phone?: string
  address?: string
  language?: string
  custom_link?: string  // Custom URL or additional information link
  referred_by?: string  // Track who referred this customer
  created_at: string
  updated_at: string
  created_by?: number
  updated_by?: number
}

// Utility function to get customer's display name
export function getCustomerDisplayName(customer: Customer): string {
  if (customer.first_name && customer.last_name) {
    return `${customer.first_name} ${customer.last_name}`
  }
  return customer.name || 'Unknown'
}

export interface MortgageApplication {
  id: number
  customer_id: number
  amount: number
  term: number
  rate: number
  status: 'pending' | 'approved' | 'denied' | 'cancelled'
  created_at: string
  updated_at: string
  created_by?: number
  updated_by?: number
}

export interface CustomerNote {
  id: number
  customer_id: number
  content: string
  created_at: string
  updated_at: string
  customer_name?: string
  created_by_username?: string
}

export interface CustomerTask {
  id: number
  customer_id: number
  description: string
  due_date: string
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  priority?: 'low' | 'medium' | 'high'
  created_at: string
  updated_at: string
  customer_name?: string
}

export interface CustomerProfile extends Customer {
  mortgageApplications?: MortgageApplication[]
  notes?: CustomerNote[]
  tasks?: CustomerTask[]
  totalNotes?: number
  totalTasks?: number
  activeTasks?: number
}

export interface CustomerFilters {
  search?: string
  sortBy?: 'name' | 'email' | 'created_at' | 'updated_at'
  sortDirection?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export interface CustomerFormData {
  name?: string  // Legacy field - will be null for new customers
  first_name?: string
  last_name?: string
  email: string
  phone?: string
  language?: string
  custom_link?: string  // Custom URL or additional information link
  referred_by?: string  // Track who referred this customer
}

export interface CustomerValidationError {
  field: string
  message: string
}

// URL validation helper function
export const isValidUrl = (url: string): boolean => {
  if (!url.trim()) return true // Empty URLs are valid (optional field)
  
  try {
    const urlObj = new URL(url)
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
  } catch {
    // If URL constructor fails, check if it starts with http/https
    const trimmedUrl = url.trim().toLowerCase()
    return trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')
  }
}

// Utility functions for customer operations
export const validateCustomerForm = (data: CustomerFormData): CustomerValidationError[] => {
  const errors: CustomerValidationError[] = []

  // Name validation - require either full name or first/last names
  if (data.first_name && data.last_name) {
    // Validate first name
    if (data.first_name.trim().length < 2) {
      errors.push({ field: 'first_name', message: 'First name must be at least 2 characters long' })
    } else if (data.first_name.trim().length > 50) {
      errors.push({ field: 'first_name', message: 'First name must be less than 50 characters' })
    }
    
    // Validate last name
    if (data.last_name.trim().length < 2) {
      errors.push({ field: 'last_name', message: 'Last name must be at least 2 characters long' })
    } else if (data.last_name.trim().length > 50) {
      errors.push({ field: 'last_name', message: 'Last name must be less than 50 characters' })
    }
  } else if (data.name) {
    // Validate legacy name field
    if (data.name.trim().length < 2) {
      errors.push({ field: 'name', message: 'Name must be at least 2 characters long' })
    } else if (data.name.trim().length > 100) {
      errors.push({ field: 'name', message: 'Name must be less than 100 characters' })
    }
  } else {
    // No name provided in any format
    errors.push({ field: 'first_name', message: 'First name is required' })
    errors.push({ field: 'last_name', message: 'Last name is required' })
  }

  // Email validation
  if (!data.email || data.email.trim().length === 0) {
    errors.push({ field: 'email', message: 'Email is required' })
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(data.email.trim())) {
      errors.push({ field: 'email', message: 'Please enter a valid email address' })
    }
  }

  // Phone validation (optional)
  if (data.phone && data.phone.trim().length > 0) {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
    if (!phoneRegex.test(data.phone.replace(/[\s\-\(\)]/g, ''))) {
      errors.push({ field: 'phone', message: 'Please enter a valid phone number' })
    }
  }

  // Validate custom_link if provided
  if (data.custom_link && data.custom_link.trim()) {
    if (!isValidUrl(data.custom_link)) {
      errors.push({ field: 'custom_link', message: 'Please enter a valid URL (must start with http:// or https://)' })
    }
  }

  return errors
}

export const formatCustomerData = (data: CustomerFormData): CustomerFormData => {
  const formatted: CustomerFormData = {
    email: data.email.trim().toLowerCase(),
    phone: data.phone?.trim() || undefined,
    language: data.language || 'fr'
  }

  // Handle both name formats
  if (data.first_name && data.last_name) {
    formatted.first_name = data.first_name.trim()
    formatted.last_name = data.last_name.trim()
  } else if (data.name) {
    formatted.name = data.name.trim()
  }

  // Format custom_link if provided
  if (data.custom_link && data.custom_link.trim()) {
    formatted.custom_link = data.custom_link.trim()
  }

  // Format referred_by if provided
  if (data.referred_by && data.referred_by.trim()) {
    formatted.referred_by = data.referred_by.trim()
  }

  return formatted
}

// Date formatting utilities
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor(diffMs / (1000 * 60))

  if (diffDays > 30) {
    return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`
  } else if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  } else if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`
  } else {
    return 'Just now'
  }
}

// Customer filtering and sorting
export const filterCustomers = (customers: Customer[], filters: CustomerFilters): Customer[] => {
  let filtered = [...customers]

  // Apply search filter
  if (filters.search && filters.search.trim()) {
    const searchTerm = filters.search.toLowerCase()
    filtered = filtered.filter(customer =>
      getCustomerDisplayName(customer).toLowerCase().includes(searchTerm) ||
      customer.email.toLowerCase().includes(searchTerm) ||
      (customer.phone && customer.phone.toLowerCase().includes(searchTerm)) ||
      (customer.address && customer.address.toLowerCase().includes(searchTerm))
    )
  }

  // Apply sorting
  if (filters.sortBy) {
    filtered.sort((a, b) => {
      let aValue: any = a[filters.sortBy!]
      let bValue: any = b[filters.sortBy!]

      // Handle date sorting
      if (filters.sortBy === 'created_at' || filters.sortBy === 'updated_at') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      } else {
        // String comparison
        aValue = aValue?.toString().toLowerCase() || ''
        bValue = bValue?.toString().toLowerCase() || ''
      }

      if (aValue < bValue) {
        return filters.sortDirection === 'desc' ? 1 : -1
      }
      if (aValue > bValue) {
        return filters.sortDirection === 'desc' ? -1 : 1
      }
      return 0
    })
  }

  return filtered
}

// API utility functions
export const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }
}

// Helper function to make API calls
export const makeApiCall = async (path: string, options: RequestInit = {}): Promise<Response> => {
  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  })
  
  // Check for 401 Unauthorized (token expired)
  if (response.status === 401) {
    console.log('🔐 Token expired - triggering automatic logout')
    // Clear token and redirect to login
    localStorage.removeItem('token')
    // Dispatch a custom event that the AuthContext can listen to
    window.dispatchEvent(new CustomEvent('tokenExpired'))
    // Show user-friendly notification
    alert('Your session has expired. Please log in again.')
    // Redirect to login page
    window.location.href = '/login'
  }
  
  // Check for 403 Forbidden (subscription required)
  if (response.status === 403) {
    const data = await response.clone().json().catch(() => ({}))
    const message = data.message || data.error || ''
    
    // Check if it's a subscription/tier-related error
    if (message.toLowerCase().includes('subscription') || 
        message.toLowerCase().includes('tier') ||
        message.toLowerCase().includes('upgrade') ||
        message.toLowerCase().includes('write access') ||
        message.toLowerCase().includes('delete access') ||
        message.toLowerCase().includes('limit reached') ||
        message.toLowerCase().includes('limit exceeded')) {
      console.log('🚫 Subscription required - redirecting to membership page')
      // Redirect to membership page with error message
      window.location.href = `/dashboard/settings/membership?error=${encodeURIComponent(message)}`
      // Return the response to prevent further processing
      return response
    }
  }
  
  return response
}

// Helper function for POST/PUT requests
export const makeJsonApiCall = async (path: string, method: 'POST' | 'PUT' | 'DELETE', body: any): Promise<Response> => {
  return makeApiCall(path, {
    method,
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })
}

export const handleApiError = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message
  }
  if (error?.message) {
    return error.message
  }
  return 'An unexpected error occurred'
}

// Customer status and statistics utilities
export const getCustomerStats = (customers: Customer[]): {
  total: number
  recentlyAdded: number
  withPhone: number
  withAddress: number
} => {
  const now = new Date()
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

  return {
    total: customers.length,
    recentlyAdded: customers.filter(c => new Date(c.created_at) > thirtyDaysAgo).length,
    withPhone: customers.filter(c => c.phone && c.phone.trim().length > 0).length,
    withAddress: customers.filter(c => c.address && c.address.trim().length > 0).length
  }
}

// Mortgage application utilities
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}

export const getMortgageStatusColor = (status: string): string => {
  switch (status) {
    case 'approved':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    case 'denied':
      return 'bg-red-100 text-red-800 border-red-200'
    case 'cancelled':
      return 'bg-gray-100 text-gray-800 border-gray-200'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

export const getTaskStatusColor = (status: string): string => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'in_progress':
      return 'bg-blue-100 text-blue-800 border-blue-200'
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    case 'cancelled':
      return 'bg-gray-100 text-gray-800 border-gray-200'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

export const getPriorityColor = (priority?: string): string => {
  switch (priority) {
    case 'high':
      return 'bg-red-100 text-red-800 border-red-200'
    case 'medium':
      return 'bg-orange-100 text-orange-800 border-orange-200'
    case 'low':
      return 'bg-green-100 text-green-800 border-green-200'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

// Phone number formatting utility
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '')
  
  // Format as (XXX) XXX-XXXX for US numbers
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  
  // For international numbers or other formats, return as-is
  return phone
}

// Customer search utilities
export const highlightSearchTerm = (text: string, searchTerm: string): string => {
  if (!searchTerm.trim()) return text
  
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  return text.replace(regex, '<mark class="bg-yellow-200">$1</mark>')
}

// Export default utility object
export const customerUtils = {
  validateCustomerForm,
  formatCustomerData,
  formatDate,
  formatDateTime,
  getRelativeTime,
  filterCustomers,
  getAuthHeaders,
  handleApiError,
  getCustomerStats,
  formatCurrency,
  getMortgageStatusColor,
  getTaskStatusColor,
  getPriorityColor,
  formatPhoneNumber,
  highlightSearchTerm
} 